import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const INK    = '#0d0d0d';
const BLUE   = '#1755f4';
const BORDER = '#e8e8e8';
const MUTED  = '#6b7280';

export default function MieterRegister() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');

  const [invitation, setInvitation] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState({ password: '', confirm: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [done, setDone]             = useState(false);

  useEffect(() => {
    if (!token) { setTokenError('Kein Einladungstoken gefunden.'); setLoading(false); return; }
    supabase
      .from('tenant_invitations')
      .select('id, tenant_id, email, expires_at, used_at, tenants(first_name, last_name)')
      .eq('token', token)
      .single()
      .then(({ data, error: fetchErr }) => {
        if (fetchErr || !data) { setTokenError('Ungültiger oder abgelaufener Einladungslink.'); }
        else if (data.used_at) { setTokenError('Dieser Einladungslink wurde bereits verwendet.'); }
        else if (new Date(data.expires_at) < new Date()) { setTokenError('Dieser Einladungslink ist abgelaufen. Bitte kontaktiere deinen Vermieter.'); }
        else { setInvitation(data); }
        setLoading(false);
      });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwörter stimmen nicht überein.'); return; }
    if (form.password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
    setSaving(true);

    // Register via Supabase Auth
    const { data, error: signUpErr } = await supabase.auth.signUp({
      email: invitation.email,
      password: form.password,
      options: { data: { role: 'mieter', full_name: `${invitation.tenants?.first_name || ''} ${invitation.tenants?.last_name || ''}`.trim() } },
    });

    if (signUpErr) { setError(signUpErr.message); setSaving(false); return; }

    if (data.user) {
      // Link portal_user_id to tenant record
      await supabase
        .from('tenants')
        .update({ portal_user_id: data.user.id, portal_enabled: true })
        .eq('id', invitation.tenant_id);

      // Mark invitation as used
      await supabase
        .from('tenant_invitations')
        .update({ used_at: new Date().toISOString() })
        .eq('id', invitation.id);

      // Set profile role
      await supabase
        .from('profiles')
        .update({ role: 'mieter' })
        .eq('id', data.user.id);
    }

    setSaving(false);
    setDone(true);
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: INK, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Lädt…</span>
    </div>
  );

  if (tokenError) return (
    <div style={{ minHeight: '100vh', background: INK, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <div style={{ background: 'white', padding: '48px 40px', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: INK, marginBottom: '32px' }}>
          IMMO<span style={{ color: BLUE }}>HQ</span>
        </div>
        <div style={{ width: '48px', height: '48px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 900, letterSpacing: '-0.5px', color: INK, marginBottom: '12px' }}>Einladung ungültig</h2>
        <p style={{ fontSize: '14px', color: MUTED, lineHeight: '1.6', margin: 0 }}>{tokenError}</p>
      </div>
    </div>
  );

  if (done) return (
    <div style={{ minHeight: '100vh', background: INK, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <div style={{ background: 'white', padding: '48px 40px', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: INK, marginBottom: '32px' }}>
          IMMO<span style={{ color: BLUE }}>HQ</span>
        </div>
        <div style={{ width: '48px', height: '48px', background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 900, letterSpacing: '-0.5px', color: INK, marginBottom: '12px' }}>Konto erstellt</h2>
        <p style={{ fontSize: '14px', color: MUTED, lineHeight: '1.6', marginBottom: '24px' }}>
          Bitte bestätige deine E-Mail-Adresse <strong style={{ color: INK }}>{invitation.email}</strong>, dann kannst du dich anmelden.
        </p>
        <button
          onClick={() => navigate('/login')}
          style={{ background: INK, color: 'white', border: 'none', padding: '12px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Zur Anmeldung
        </button>
      </div>
    </div>
  );

  const tenant = invitation?.tenants;

  return (
    <div style={{ minHeight: '100vh', background: INK, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <div style={{ background: 'white', padding: '48px 40px', maxWidth: '440px', width: '100%' }}>
        <div style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: INK, marginBottom: '32px' }}>
          IMMO<span style={{ color: BLUE }}>HQ</span>
          <span style={{ marginLeft: '12px', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', color: MUTED }}>MIETERPORTAL</span>
        </div>

        <h1 style={{ fontSize: '22px', fontWeight: 900, letterSpacing: '-0.5px', color: INK, marginBottom: '8px' }}>
          Willkommen, {tenant?.first_name || ''}!
        </h1>
        <p style={{ fontSize: '13px', color: MUTED, marginBottom: '28px', lineHeight: '1.6' }}>
          Erstelle ein Passwort für dein Mieterportal-Konto.
        </p>

        <div style={{ background: '#f5f5f5', padding: '12px 16px', marginBottom: '24px', fontSize: '12px', color: MUTED }}>
          <strong style={{ color: INK }}>E-Mail:</strong> {invitation.email}
        </div>

        <form onSubmit={handleSubmit}>
          {[
            { label: 'Passwort', key: 'password' },
            { label: 'Passwort bestätigen', key: 'confirm' },
          ].map(({ label, key }) => (
            <div key={key} style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' }}>
                {label}
              </label>
              <input
                type="password"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder="min. 8 Zeichen"
                style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 0, padding: '10px 12px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', color: INK, outline: 'none' }}
              />
            </div>
          ))}

          {error && (
            <p style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={saving}
            style={{ width: '100%', background: saving ? MUTED : INK, color: 'white', border: 'none', padding: '13px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            {saving ? 'Konto wird erstellt…' : 'Konto erstellen'}
          </button>
        </form>
      </div>
    </div>
  );
}
